import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Stat } from "@/components/admin/Ui";
import { Sparkle, Lightning } from "@phosphor-icons/react";

export default function Autopilot() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastScan, setLastScan] = useState(null);

  const load = async () => {
    const { data } = await api.get("/autopilot");
    setData(data);
    setCfg(data.config);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/autopilot", {
        auto_outreach_enabled: cfg.auto_outreach_enabled,
        min_score: Number(cfg.min_score),
        daily_cap: Number(cfg.daily_cap),
        auto_approve_apps: cfg.auto_approve_apps,
      });
      toast.success("Autopilot config saved");
      await load();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const scan = async () => {
    setBusy(true);
    setLastScan(null);
    try {
      const { data } = await api.post("/autopilot/scan");
      setLastScan(data);
      toast.success(`Scored ${data.scored} · Drafted ${data.drafted} · Sent ${data.sent_mock}`);
      await load();
    } catch (e) { toast.error("Scan failed"); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="p-8 text-navy/60">Loading…</div>;

  return (
    <div data-testid="admin-autopilot-page">
      <PageHeader title="Autopilot" subtitle="AI Decision Autonomy" action={
        <button onClick={scan} disabled={busy} data-testid="autopilot-run-button" className="btn-primary">
          <Sparkle size={14} /> {busy ? "Scanning…" : "Run scan now"}
        </button>
      } />
      <div className="p-8 space-y-6">
        {/* Hero toggle */}
        <div className={`p-6 border ${cfg.auto_outreach_enabled ? "border-emerald-300 bg-emerald-50" : "border-black/10 bg-white"} flex items-start justify-between gap-6`}>
          <div className="flex items-start gap-4">
            <Lightning size={28} weight="duotone" className={cfg.auto_outreach_enabled ? "text-emerald-600" : "text-navy/40"} />
            <div>
              <div className="font-display font-bold text-xl text-navy">Autonomous outreach</div>
              <p className="text-sm text-navy/70 mt-1 max-w-xl leading-relaxed">
                When ON, prospects scoring ≥ <span className="font-semibold">{cfg.min_score}</span> are
                auto-drafted AND auto-sent (mocked send) — up to the daily cap of <span className="font-semibold">{cfg.daily_cap}</span>.
                Everything else lands in your review queue.
              </p>
            </div>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              data-testid="autopilot-toggle"
              className="sr-only peer"
              checked={cfg.auto_outreach_enabled}
              onChange={(e) => setCfg({ ...cfg, auto_outreach_enabled: e.target.checked })}
            />
            <div className="w-12 h-6 bg-navy/15 peer-checked:bg-emerald-600 rounded-full transition-colors relative after:absolute after:left-0.5 after:top-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full after:transition-transform peer-checked:after:translate-x-6" />
          </label>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Sent today" value={data?.sent_today ?? 0} hint={`cap ${cfg.daily_cap}`} />
          <Stat label="In review queue" value={data?.in_review_queue ?? 0} hint="drafts waiting" />
          <Stat label="Min score" value={cfg.min_score} hint="qualification bar" />
          <Stat label="Daily cap" value={cfg.daily_cap} hint="sends per 24h" />
        </div>

        {/* Settings card */}
        <div className="bg-white border border-black/10 p-6">
          <div className="overline mb-4">Configuration</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
            <Field label="Min score for outreach">
              <input data-testid="autopilot-min-score" type="number" min="0" max="10" value={cfg.min_score}
                onChange={(e) => setCfg({ ...cfg, min_score: e.target.value })}
                className={input} />
            </Field>
            <Field label="Daily send cap">
              <input data-testid="autopilot-daily-cap" type="number" min="1" value={cfg.daily_cap}
                onChange={(e) => setCfg({ ...cfg, daily_cap: e.target.value })}
                className={input} />
            </Field>
            <Field label="Auto-approve apps ≥ 9 (no risk flags)">
              <label className="flex items-center gap-2 mt-2">
                <input data-testid="autopilot-auto-approve" type="checkbox" checked={cfg.auto_approve_apps}
                  onChange={(e) => setCfg({ ...cfg, auto_approve_apps: e.target.checked })} />
                <span className="text-sm text-navy/70">Enable hands-free</span>
              </label>
            </Field>
          </div>
          <button onClick={save} disabled={busy} data-testid="autopilot-save-button" className="btn-primary mt-6">
            {busy ? "Saving…" : "Save autopilot config"}
          </button>
        </div>

        {lastScan && (
          <div className="bg-white border border-black/10 p-6" data-testid="autopilot-scan-result">
            <div className="overline mb-3">Last scan result</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Stat label="Scored" value={lastScan.scored} />
              <Stat label="Drafted" value={lastScan.drafted} />
              <Stat label="Sent (mock)" value={lastScan.sent_mock} />
              <Stat label="Budget left" value={lastScan.budget_remaining} hint="sends remaining today" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const input = "w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy";
const Field = ({ label, children }) => (
  <div><label className="overline mb-2 block">{label}</label>{children}</div>
);
